// VehicleCatalog — admin editor for the vehicle catalog hierarchy:
//   Makes → Models → Entries
//
// Layout mirrors /attributes: left sidebar lists makes (with model count),
// clicking a make reveals its models; clicking a model lists entries;
// selecting an entry opens the right-pane editor.
//
// The entry editor has:
//   - Spec fields (passenger, doors, gear shift, baggage, fuel type, body type)
//   - Per-locale tabs (en/fr/ar/es) for title / description / SEO copy
//   - Feature multi-select (attribute_values from the search index)
//   - Image manager (presign → browser PUT → finalize)
//
// Save is transactional on the server: one POST/PATCH replaces all locale
// rows + feature links + updates specs atomically.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { PageHeader } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchCatalogMakes,
  createCatalogMake,
  deleteCatalogMake,
  fetchCatalogModels,
  createCatalogModel,
  deleteCatalogModel,
  fetchCatalogEntries,
  fetchCatalogEntry,
  saveCatalogEntry,
  deleteCatalogEntry,
  fetchAdminAttributeValues,
  presignCatalogImage,
  finalizeCatalogImage,
  retrieveCatalogMetadata,
  retrieveCatalogImage,
  bulkFillCatalogMetadata,
  generateCatalogMetadataForNew,
  reorderCatalogImages,
  setCatalogImageHero,
  deleteCatalogImage,
  type CatalogLocale,
  type CatalogLocaleCopy,
  type CatalogSpecs,
  type CatalogEntryRecord,
  type CatalogMakeRow,
  type CatalogEntryListRow,
  type CatalogEntryImage,
} from '@/lib/admin-api';

const LOCALES: CatalogLocale[] = ['en', 'fr', 'ar', 'es'];
const LOCALE_LABEL: Record<CatalogLocale, string> = {
  en: 'English',
  fr: 'Français',
  ar: 'العربية',
  es: 'Español',
};

function emptyLocales(): Record<CatalogLocale, CatalogLocaleCopy> {
  return { en: {}, fr: {}, ar: {}, es: {} };
}

function emptySpecs(): CatalogSpecs {
  return {
    passenger: null,
    doors: null,
    gearShift: null,
    baggage: null,
    fuelType: null,
    bodyType: null,
  };
}

// ─── Entry draft shape ───────────────────────────────────────────────

interface EntryDraft {
  modelId: number;
  /** Null = no model year (listing-seeded entries); clearing the input sends null. */
  year: number | null;
  trim: string;
  isActive: boolean;
  specs: CatalogSpecs;
  featureIds: number[];
  locales: Record<CatalogLocale, CatalogLocaleCopy>;
}

// ─── AI retrieve merge helper ────────────────────────────────────────
//
// Fill-only-empty: keeps the existing draft value if it is already set, otherwise
// uses the AI-retrieved value. Does NOT touch year/trim/isActive/featureIds/modelId.

function mergeEmpty(
  draft: EntryDraft,
  meta: { specs: CatalogSpecs; locales: Record<CatalogLocale, CatalogLocaleCopy> },
): EntryDraft {
  const SPEC_KEYS = ['passenger', 'doors', 'gearShift', 'baggage', 'fuelType', 'bodyType'] as const;
  const LOCALE_KEYS = ['title', 'description', 'seoMetaTitle', 'seoMetaDescription'] as const;

  const mergedSpecs = { ...draft.specs };
  for (const k of SPEC_KEYS) {
    const existing = draft.specs[k];
    if (existing === null || existing === undefined || existing === '') {
      const incoming = meta.specs[k];
      if (incoming !== null && incoming !== undefined && incoming !== '') {
        (mergedSpecs as Record<string, unknown>)[k] = incoming;
      }
    }
  }

  const mergedLocales = { ...draft.locales } as Record<CatalogLocale, CatalogLocaleCopy>;
  const ALL_LOCALES: CatalogLocale[] = ['en', 'fr', 'ar', 'es'];
  for (const loc of ALL_LOCALES) {
    const existing = draft.locales[loc] ?? {};
    const incoming = meta.locales[loc] ?? {};
    const merged: CatalogLocaleCopy = { ...existing };
    for (const k of LOCALE_KEYS) {
      const ev = existing[k];
      if (!ev) {
        const iv = incoming[k];
        if (iv) (merged as Record<string, unknown>)[k] = iv;
      }
    }
    mergedLocales[loc] = merged;
  }

  return { ...draft, specs: mergedSpecs, locales: mergedLocales };
}

// The GET /entries/:id payload reads the `text`-typed spec_* columns, so numeric
// spec counts arrive as strings ("7") rather than numbers. If we drop those into
// the draft verbatim, any spec field the operator leaves untouched rides back to
// the API as a string, which the write schema used to reject ("Expected number,
// received string"). Normalize to number|null on ingest so the draft is always
// correctly typed regardless of which fields get edited. (The API schema now also
// coerces these, so old deployed UIs keep working — but the form should be correct.)
function toSpecInt(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isNaN(n) ? null : n;
}

function normalizeSpecs(s: CatalogSpecs): CatalogSpecs {
  return {
    ...s,
    passenger: toSpecInt(s.passenger),
    doors: toSpecInt(s.doors),
    baggage: toSpecInt(s.baggage),
  };
}

function recordToDraft(r: CatalogEntryRecord): EntryDraft {
  return {
    modelId: r.modelId,
    year: r.year,
    trim: r.trim,
    isActive: r.isActive,
    specs: normalizeSpecs(r.specs),
    featureIds: r.featureIds,
    locales: {
      en: r.locales.en ?? {},
      fr: r.locales.fr ?? {},
      ar: r.locales.ar ?? {},
      es: r.locales.es ?? {},
    },
  };
}

// ─── Root component ──────────────────────────────────────────────────

export function VehicleCatalog() {
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { entry?: number };
  const makesQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'makes'],
    queryFn: ({ signal }) => fetchCatalogMakes(signal),
  });

  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<number | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<number | null>(null);
  const [creatingMake, setCreatingMake] = useState(false);
  const [creatingModel, setCreatingModel] = useState(false);
  const [creatingEntry, setCreatingEntry] = useState(false);

  // "Fill ALL with AI": walk every active entry through the bulk-fill endpoint,
  // which processes a bounded batch per request and returns a cursor. We loop the
  // cursor client-side so a full catalog run survives the gateway timeout. Live
  // progress is surfaced in the button; failures are accumulated and reported.
  const [bulkProgress, setBulkProgress] = useState<{
    running: boolean;
    updated: number;
    failed: number;
    error: string | null;
    done: boolean;
  }>({ running: false, updated: 0, failed: 0, error: null, done: false });

  const runBulkFill = async () => {
    if (bulkProgress.running) return;
    if (!window.confirm('Regenerate metadata + features for ALL active catalog entries via AI? This OVERWRITES existing copy.')) {
      return;
    }
    setBulkProgress({ running: true, updated: 0, failed: 0, error: null, done: false });
    let cursor = 0;
    let updated = 0;
    let failed = 0;
    try {
      for (;;) {
        const res = await bulkFillCatalogMetadata(cursor);
        updated += res.updated;
        failed += res.failed.length;
        setBulkProgress({ running: true, updated, failed, error: null, done: false });
        if (res.nextCursor == null) break;
        cursor = res.nextCursor;
      }
      setBulkProgress({ running: false, updated, failed, error: null, done: true });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'vehicle-catalog'] });
    } catch (err) {
      setBulkProgress({
        running: false,
        updated,
        failed,
        error: (err as Error).message === 'ai_not_configured'
          ? 'AI is not configured (OPENAI_API_KEY unset).'
          : (err as Error).message,
        done: false,
      });
    }
  };
  // ?entry=<id> deep-link (e.g. from a car listing's Photos panel): resolve the
  // entry once, preselect its make/model/entry. Consumed-id ref keeps manual
  // navigation inside the page from being fought by the effect; a changed
  // ?entry (another car's panel) re-resolves. fetchQuery shares the queryKey
  // the entry editor + CarDetail's Photos panel use, so the pre-warmed cache
  // is hit instead of refetching.
  const [deepLinkPending, setDeepLinkPending] = useState(search.entry != null);
  const consumedEntryRef = useRef<number | null>(null);
  useEffect(() => {
    const target = search.entry;
    if (target == null || consumedEntryRef.current === target) {
      setDeepLinkPending(false);
      return;
    }
    consumedEntryRef.current = target;
    let cancelled = false;
    void queryClient
      .fetchQuery({
        queryKey: ['admin', 'vehicle-catalog', 'entry', target],
        queryFn: ({ signal }) => fetchCatalogEntry(target, signal),
      })
      .then((rec) => {
        if (cancelled) return;
        if (rec.makeId != null) setSelectedMake(rec.makeId);
        setSelectedModel(rec.modelId);
        setSelectedEntry(rec.id);
      })
      .catch(() => {
        // Unknown/deleted entry — fall back to the default make selection.
      })
      .finally(() => {
        if (!cancelled) setDeepLinkPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [search.entry, queryClient]);

  const makes = makesQ.data?.rows ?? [];

  // Auto-select first make when list loads (skipped while a deep-link is
  // resolving so the target selection doesn't flash to makes[0]).
  useEffect(() => {
    if (deepLinkPending) return;
    if (selectedMake === null && makes.length > 0 && !creatingMake) {
      setSelectedMake(makes[0]?.id ?? null);
    }
  }, [makes, selectedMake, creatingMake, deepLinkPending]);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Vehicle Catalog"
        sub="Master catalog of vehicle makes, models, and trim-year entries with localized copy, specs, features, and images. Changes apply to new car listings immediately."
        actions={
          <div className="flex items-center gap-2">
            <RowAction
              kind="ghost"
              disabled={bulkProgress.running}
              busy={bulkProgress.running}
              onClick={runBulkFill}
            >
              {bulkProgress.running
                ? `Filling… ${bulkProgress.updated} done${bulkProgress.failed ? `, ${bulkProgress.failed} failed` : ''}`
                : bulkProgress.done
                  ? `Filled ${bulkProgress.updated}${bulkProgress.failed ? ` · ${bulkProgress.failed} failed` : ''} ✓`
                  : 'Fill ALL with AI'}
            </RowAction>
            <RowAction
              kind="ghost"
              onClick={() => {
                setCreatingMake(true);
                setSelectedMake(null);
                setSelectedModel(null);
                setSelectedEntry(null);
              }}
            >
              New make
            </RowAction>
            {selectedMake != null && !creatingMake && (
              <RowAction
                kind="ghost"
                onClick={() => {
                  setCreatingModel(true);
                  setSelectedEntry(null);
                }}
              >
                New model
              </RowAction>
            )}
            {selectedModel != null && !creatingModel && !creatingMake && (
              <RowAction
                kind="primary"
                onClick={() => {
                  setCreatingEntry(true);
                  setSelectedEntry(null);
                }}
              >
                New entry
              </RowAction>
            )}
          </div>
        }
      />

      {makesQ.error ? (
        <ErrorPanel message={(makesQ.error as Error).message} />
      ) : null}
      {bulkProgress.error ? (
        <ErrorPanel message={`Fill ALL with AI: ${bulkProgress.error}`} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_260px_1fr]">
        {/* Column 1 — makes */}
        <aside className="space-y-3">
          <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-3">
            <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
              Makes
            </div>
            {creatingMake ? (
              <CreateMakeForm
                onCancel={() => setCreatingMake(false)}
                onCreated={async (id) => {
                  setCreatingMake(false);
                  setSelectedMake(id);
                  setSelectedModel(null);
                  setSelectedEntry(null);
                  await queryClient.invalidateQueries({
                    queryKey: ['admin', 'vehicle-catalog', 'makes'],
                  });
                }}
              />
            ) : (
              <MakeList
                rows={makes}
                selected={selectedMake}
                loading={makesQ.isLoading}
                onSelect={(id) => {
                  setSelectedMake(id);
                  setSelectedModel(null);
                  setSelectedEntry(null);
                  setCreatingModel(false);
                  setCreatingEntry(false);
                }}
                onDeleted={async () => {
                  setSelectedMake(null);
                  setSelectedModel(null);
                  setSelectedEntry(null);
                  await queryClient.invalidateQueries({
                    queryKey: ['admin', 'vehicle-catalog', 'makes'],
                  });
                }}
              />
            )}
          </div>
        </aside>

        {/* Column 2 — models (own always-present column, next to makes) */}
        <aside className="space-y-3 lg:sticky lg:top-4 lg:self-start">
          {selectedMake != null && !creatingMake ? (
            <ModelPanel
              makeId={selectedMake}
              selected={selectedModel}
              creating={creatingModel}
              onSelect={(id) => {
                setSelectedModel(id);
                setSelectedEntry(null);
                setCreatingEntry(false);
              }}
              onCreateDone={async (id) => {
                setCreatingModel(false);
                setSelectedModel(id);
                setSelectedEntry(null);
                await queryClient.invalidateQueries({
                  queryKey: ['admin', 'vehicle-catalog', 'models', selectedMake],
                });
              }}
              onCancelCreate={() => setCreatingModel(false)}
              onDeleted={async () => {
                setSelectedModel(null);
                setSelectedEntry(null);
                await queryClient.invalidateQueries({
                  queryKey: ['admin', 'vehicle-catalog', 'models', selectedMake],
                });
              }}
            />
          ) : (
            <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-3">
              <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                Models
              </div>
              <div className="px-1 py-2 text-[12px] text-[color:var(--bo-mute)]">
                Select a make to see its models.
              </div>
            </div>
          )}
        </aside>

        {/* Right pane — entries list + editor */}
        <div className="space-y-4">
          {selectedModel != null && !creatingMake && !creatingModel ? (
            <EntryPane
              makeId={selectedMake!}
              modelId={selectedModel}
              selectedEntry={selectedEntry}
              creating={creatingEntry}
              onSelectEntry={(id) => {
                setSelectedEntry(id);
                setCreatingEntry(false);
              }}
              onCreateEntry={() => {
                setCreatingEntry(true);
                setSelectedEntry(null);
              }}
              onCancelCreate={() => setCreatingEntry(false)}
              onCreated={(id) => {
                setCreatingEntry(false);
                setSelectedEntry(id);
              }}
              onDeleted={() => {
                setSelectedEntry(null);
              }}
            />
          ) : (
            <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
              {makesQ.isLoading
                ? 'Loading…'
                : makes.length === 0
                ? 'No makes yet. Click "New make" to seed the catalog.'
                : selectedMake == null
                ? 'Select a make on the left to browse models and entries.'
                : 'Select a model to browse entries.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Make list ────────────────────────────────────────────────────────

function MakeList({
  rows,
  selected,
  loading,
  onSelect,
  onDeleted,
}: {
  rows: CatalogMakeRow[];
  selected: number | null;
  loading: boolean;
  onSelect: (id: number) => void;
  onDeleted: () => void;
}) {
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteCatalogMake(id),
    onSuccess: onDeleted,
  });

  if (loading) {
    return (
      <div className="px-1 py-2 text-[12px] text-[color:var(--bo-mute)]">Loading makes…</div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="px-1 py-2 text-[12px] text-[color:var(--bo-mute)]">
        No makes yet.
      </div>
    );
  }
  return (
    <ul className="space-y-0.5">
      {rows.map((row) => {
        const isActive = row.id === selected;
        return (
          <li key={row.id}>
            <div
              className={`group flex items-center justify-between gap-1 rounded-md px-2 py-1.5 text-[12.5px] transition ${
                isActive
                  ? 'bg-[color:var(--bo-bg)] font-semibold text-[color:var(--bo-ink)]'
                  : 'text-[color:var(--bo-ink)]/80 hover:bg-[color:var(--bo-bg)]'
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(row.id)}
                className="flex-1 text-start"
              >
                {row.name}
              </button>
              <button
                type="button"
                onClick={() => {
                  const ok = window.confirm(`Delete make "${row.name}"? All its models and entries will be deleted.`);
                  if (ok) deleteMut.mutate(row.id);
                }}
                className="hidden h-5 w-5 items-center justify-center rounded text-[#9f1239] opacity-0 hover:bg-[#fff1f2] group-hover:opacity-100 lg:flex"
                title="Delete make"
              >
                ×
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Create make ─────────────────────────────────────────────────────

function CreateMakeForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: number) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const mut = useMutation({
    mutationFn: () => createCatalogMake({ slug: slug.trim(), name: name.trim() }),
    onSuccess: (res) => onCreated(res.id),
  });

  return (
    <div className="space-y-2 rounded-lg border border-[color:var(--bo-line)] bg-white p-3">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-semibold">New make</span>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
        >
          Cancel
        </button>
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (!slugTouched) setSlug(slugify(e.target.value));
        }}
        placeholder="Name (e.g. Toyota)"
        className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
      />
      <input
        type="text"
        value={slug}
        onChange={(e) => {
          setSlug(e.target.value);
          setSlugTouched(true);
        }}
        placeholder="slug (e.g. toyota)"
        className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 font-mono text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
      />
      {mut.error ? (
        <div className="text-[11px] text-[#9f1239]">{(mut.error as Error).message}</div>
      ) : null}
      <RowAction
        kind="primary"
        disabled={!name.trim() || !slug.trim() || mut.isPending}
        busy={mut.isPending}
        onClick={() => mut.mutate()}
      >
        Create
      </RowAction>
    </div>
  );
}

// ─── Model panel ─────────────────────────────────────────────────────

function ModelPanel({
  makeId,
  selected,
  creating,
  onSelect,
  onCreateDone,
  onCancelCreate,
  onDeleted,
}: {
  makeId: number;
  selected: number | null;
  creating: boolean;
  onSelect: (id: number) => void;
  onCreateDone: (id: number) => void | Promise<void>;
  onCancelCreate: () => void;
  onDeleted: () => void | Promise<void>;
}) {
  const modelsQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'models', makeId],
    queryFn: ({ signal }) => fetchCatalogModels(makeId, signal),
  });
  const models = modelsQ.data?.rows ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteCatalogModel(id),
    onSuccess: onDeleted,
  });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const createMut = useMutation({
    mutationFn: () => createCatalogModel({ makeId, slug: slug.trim(), name: name.trim() }),
    onSuccess: async (res) => {
      setName('');
      setSlug('');
      setSlugTouched(false);
      await onCreateDone(res.id);
    },
  });

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-3">
      <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
        Models
      </div>
      {modelsQ.isLoading ? (
        <div className="px-1 py-2 text-[12px] text-[color:var(--bo-mute)]">Loading…</div>
      ) : models.length === 0 && !creating ? (
        <div className="px-1 py-2 text-[12px] text-[color:var(--bo-mute)]">No models yet.</div>
      ) : (
        <ul className="mb-2 space-y-0.5">
          {models.map((row) => {
            const isActive = row.id === selected;
            return (
              <li key={row.id}>
                <div
                  className={`group flex items-center justify-between gap-1 rounded-md px-2 py-1.5 text-[12.5px] transition ${
                    isActive
                      ? 'bg-[color:var(--bo-bg)] font-semibold text-[color:var(--bo-ink)]'
                      : 'text-[color:var(--bo-ink)]/80 hover:bg-[color:var(--bo-bg)]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(row.id)}
                    className="flex-1 text-start"
                  >
                    {row.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const ok = window.confirm(`Delete model "${row.name}"?`);
                      if (ok) deleteMut.mutate(row.id);
                    }}
                    className="hidden h-5 w-5 items-center justify-center rounded text-[#9f1239] opacity-0 hover:bg-[#fff1f2] group-hover:opacity-100 lg:flex"
                    title="Delete model"
                  >
                    ×
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {creating && (
        <div className="space-y-2 rounded-lg border border-[color:var(--bo-line)] bg-white p-2">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
            placeholder="Name (e.g. Camry)"
            className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
          />
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            placeholder="slug (e.g. camry)"
            className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 font-mono text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
          />
          {createMut.error ? (
            <div className="text-[11px] text-[#9f1239]">
              {(createMut.error as Error).message}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <RowAction
              kind="primary"
              disabled={!name.trim() || !slug.trim() || createMut.isPending}
              busy={createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              Create
            </RowAction>
            <button
              type="button"
              onClick={onCancelCreate}
              className="text-[11px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Entry pane (list + editor) ───────────────────────────────────────

function EntryPane({
  makeId,
  modelId,
  selectedEntry,
  creating,
  onSelectEntry,
  onCreateEntry,
  onCancelCreate,
  onCreated,
  onDeleted,
}: {
  makeId: number;
  modelId: number;
  selectedEntry: number | null;
  creating: boolean;
  onSelectEntry: (id: number) => void;
  onCreateEntry: () => void;
  onCancelCreate: () => void;
  onCreated: (id: number) => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const entriesQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'entries', makeId, modelId],
    queryFn: ({ signal }) => fetchCatalogEntries({ makeId, modelId }, signal),
  });
  const entries = entriesQ.data?.rows ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteCatalogEntry(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'vehicle-catalog', 'entries', makeId, modelId],
      });
      onDeleted();
    },
  });

  return (
    <>
      {/* Entry list */}
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[13px] font-semibold tracking-tight">Entries</span>
          <RowAction kind="ghost" onClick={onCreateEntry}>
            New entry
          </RowAction>
        </div>
        {entriesQ.isLoading ? (
          <div className="text-[12px] text-[color:var(--bo-mute)]">Loading entries…</div>
        ) : entries.length === 0 && !creating ? (
          <div className="text-[12px] text-[color:var(--bo-mute)]">
            No entries yet. Click "New entry" to add one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-[color:var(--bo-line)] text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  <th className="pb-1.5 pe-3 text-start font-medium">Year</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Trim</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Title</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Status</th>
                  <th className="pb-1.5 text-end font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--bo-line)]">
                {entries.map((row) => (
                  <EntryRow
                    key={row.id}
                    row={row}
                    isSelected={row.id === selectedEntry}
                    onSelect={() => onSelectEntry(row.id)}
                    onDelete={() => {
                      const ok = window.confirm(
                        `Delete entry "${row.defaultTitle}"? This cannot be undone.`,
                      );
                      if (ok) deleteMut.mutate(row.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor / create form */}
      {creating ? (
        <EntryEditor
          key="new"
          entryId={null}
          defaultModelId={modelId}
          onCancel={onCancelCreate}
          onSaved={async (id) => {
            await queryClient.invalidateQueries({
              queryKey: ['admin', 'vehicle-catalog', 'entries', makeId, modelId],
            });
            onCreated(id);
          }}
        />
      ) : selectedEntry != null ? (
        <EntryEditor
          key={selectedEntry}
          entryId={selectedEntry}
          defaultModelId={modelId}
          onCancel={() => onSelectEntry(selectedEntry)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['admin', 'vehicle-catalog', 'entries', makeId, modelId],
            });
          }}
        />
      ) : null}
    </>
  );
}

function EntryRow({
  row,
  isSelected,
  onSelect,
  onDelete,
}: {
  row: CatalogEntryListRow;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <tr
      className={`group cursor-pointer transition-colors hover:bg-[color:var(--bo-bg)] ${
        isSelected ? 'bg-[color:var(--bo-bg)]' : ''
      }`}
    >
      <td className="py-2 pe-3 tabular-nums" onClick={onSelect}>
        {row.year}
      </td>
      <td className="py-2 pe-3" onClick={onSelect}>
        {row.trim || <span className="text-[color:var(--bo-mute)]">—</span>}
      </td>
      <td className="py-2 pe-3" onClick={onSelect}>
        <span className={isSelected ? 'font-semibold' : ''}>{row.defaultTitle}</span>
      </td>
      <td className="py-2 pe-3" onClick={onSelect}>
        <span
          className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ${
            row.isActive
              ? 'bg-[#dcfce7] text-[#166534]'
              : 'bg-[#f3f4f6] text-[color:var(--bo-mute)]'
          }`}
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="py-2 text-end">
        <button
          type="button"
          onClick={onDelete}
          className="rounded px-1.5 py-0.5 text-[10.5px] text-[#9f1239] opacity-0 hover:bg-[#fff1f2] group-hover:opacity-100"
          title="Delete entry"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ─── Entry editor ─────────────────────────────────────────────────────

function EntryEditor({
  entryId,
  defaultModelId,
  onCancel,
  onSaved,
}: {
  entryId: number | null;
  defaultModelId: number;
  onCancel: () => void;
  onSaved: (id: number) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const isNew = entryId === null;

  const recordQ = useQuery<CatalogEntryRecord, Error>({
    queryKey: ['admin', 'vehicle-catalog', 'entry', entryId],
    queryFn: ({ signal }) => fetchCatalogEntry(entryId!, signal),
    enabled: !isNew,
  });

  const [draft, setDraft] = useState<EntryDraft>(() => ({
    modelId: defaultModelId,
    year: new Date().getFullYear(),
    trim: '',
    isActive: true,
    specs: emptySpecs(),
    featureIds: [],
    locales: emptyLocales(),
  }));

  const [locale, setLocale] = useState<CatalogLocale>('en');

  // Holds a pending AI-metadata merge that must survive the next recordQ refetch.
  // The retrieve mutation writes here before invalidating the entry query; the
  // useEffect below reads and clears it so the merge is re-applied on top of the
  // fresh server record (which carries the newly-uploaded hero image).
  const pendingMetaRef = useRef<{
    specs: CatalogSpecs;
    locales: Record<CatalogLocale, CatalogLocaleCopy>;
  } | null>(null);

  useEffect(() => {
    if (recordQ.data) {
      const base = recordToDraft(recordQ.data);
      if (pendingMetaRef.current) {
        const meta = pendingMetaRef.current;
        pendingMetaRef.current = null;
        setDraft(mergeEmpty(base, meta));
      } else {
        setDraft(base);
      }
    }
  }, [recordQ.data]);

  const dirty = useMemo(() => {
    if (isNew) return true;
    if (!recordQ.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(recordToDraft(recordQ.data));
  }, [draft, recordQ.data, isNew]);

  const saveMut = useMutation({
    mutationFn: () =>
      saveCatalogEntry(entryId, {
        modelId: draft.modelId,
        year: draft.year,
        trim: draft.trim,
        isActive: draft.isActive,
        specs: draft.specs,
        featureIds: draft.featureIds,
        locales: draft.locales,
      }),
    onSuccess: async (res) => {
      if (!isNew && entryId != null) {
        await queryClient.invalidateQueries({
          queryKey: ['admin', 'vehicle-catalog', 'entry', entryId],
        });
      }
      await onSaved(res.id);
    },
  });

  // Latest draft, readable from long-running mutation callbacks without the
  // stale-closure problem (the image poll runs for minutes).
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Canonical options for the string specs, sourced from the attributes
  // catalog so entries carry the same taxonomy the storefront filters on
  // (transmission / fuel-type / vehicle-type EN names, not free text).
  const specOptionsQ = useQuery({
    queryKey: ['admin', 'attribute-values', 'picker', 'spec-options'],
    queryFn: ({ signal }) => fetchAdminAttributeValues({ limit: 200 }, signal),
    staleTime: 300_000,
  });
  const specOptions = useMemo(() => {
    const rows = specOptionsQ.data?.rows ?? [];
    const names = (slug: string) =>
      rows.filter((r) => r.attributeSlug === slug && r.name).map((r) => r.name as string);
    return {
      gearShift: names('transmission'),
      fuelType: names('fuel-type'),
      bodyType: names('vehicle-type'),
    };
  }, [specOptionsQ.data]);

  const [retrieveError, setRetrieveError] = useState<string | null>(null);

  // AI metadata: fills empty spec + locale copy fields from AI.
  //
  // The tricky ordering: invalidating the entry-record query causes the
  // useEffect above to reset the draft from the server record, which would
  // wipe the just-merged AI metadata. Fix: stash meta in pendingMetaRef
  // BEFORE invalidating; the useEffect re-applies the merge on top of the
  // fresh server data.
  const retrieveMut = useMutation({
    mutationFn: async () => {
      if (entryId == null) throw new Error('No entry id');
      const meta = await retrieveCatalogMetadata(entryId);
      // Apply merge optimistically so fields populate immediately.
      setDraft((d) => mergeEmpty(d, meta));
      // Stash meta so the upcoming refetch-triggered useEffect re-applies it.
      pendingMetaRef.current = meta;
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'vehicle-catalog', 'entry', entryId],
      });
    },
    onMutate: () => {
      setRetrieveError(null);
    },
    onError: (err: Error) => {
      pendingMetaRef.current = null;
      setRetrieveError(err.message);
    },
  });

  // AI image: generation is ASYNC server-side (202 { queued: true }) — it
  // takes 40-90s, longer than the API gateway lets a response wait. Queue it,
  // then poll the entry until the new hero image lands. Keeping the mutation
  // pending keeps the button in its loading state while the image generates.
  const imageMut = useMutation({
    mutationFn: async () => {
      if (entryId == null) throw new Error('No entry id');
      const baselineHeroId = recordQ.data?.images.find((i) => i.isHero)?.id ?? null;
      const baselineCount = recordQ.data?.images.length ?? 0;
      await retrieveCatalogImage(entryId);

      // Poll for up to 3 minutes.
      for (let attempt = 0; attempt < 36; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const fresh = await fetchCatalogEntry(entryId);
        const heroId = fresh.images.find((i) => i.isHero)?.id ?? null;
        if (heroId !== baselineHeroId || fresh.images.length !== baselineCount) {
          // The invalidate below rebuilds the draft from the fresh record;
          // stash the operator's current (possibly unsaved) specs + copy so
          // the fill-empty merge re-applies them on top.
          pendingMetaRef.current = {
            specs: draftRef.current.specs,
            locales: draftRef.current.locales,
          };
          await queryClient.invalidateQueries({
            queryKey: ['admin', 'vehicle-catalog', 'entry', entryId],
          });
          return;
        }
      }
      // Timed out waiting — the task may still finish; the image appears on
      // the next reload.
      throw new Error('image_still_generating');
    },
    onMutate: () => {
      setRetrieveError(null);
    },
    onError: (err: Error) => {
      setRetrieveError(err.message);
    },
  });

  // Create-time AI: for a not-yet-saved entry there's no id, so generate specs +
  // locale copy from the chosen model + year/trim and merge into the draft
  // (fill-empty). The operator reviews, then saves. Image generation stays
  // post-save (it needs an entry to attach to) via the Retrieve button above.
  const generateMut = useMutation({
    mutationFn: async () => {
      const meta = await generateCatalogMetadataForNew({
        modelId: draft.modelId,
        year: draft.year ?? undefined,
        trim: draft.trim || undefined,
      });
      setDraft((d) => mergeEmpty(d, meta));
    },
    onMutate: () => setRetrieveError(null),
    onError: (err: Error) => setRetrieveError(err.message),
  });

  const setSpec = <K extends keyof CatalogSpecs>(key: K, value: CatalogSpecs[K]) => {
    setDraft((d) => ({ ...d, specs: { ...d.specs, [key]: value } }));
  };

  const setLocaleCopy = <K extends keyof CatalogLocaleCopy>(
    loc: CatalogLocale,
    key: K,
    value: CatalogLocaleCopy[K],
  ) => {
    setDraft((d) => ({
      ...d,
      locales: {
        ...d.locales,
        [loc]: { ...d.locales[loc], [key]: value },
      },
    }));
  };

  if (!isNew && (recordQ.isLoading || !recordQ.data)) {
    return (
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
        {recordQ.isLoading ? 'Loading entry…' : <ErrorPanel message={(recordQ.error as Error).message} />}
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
              {isNew ? 'New entry' : 'Entry'}
            </div>
            <div className="text-[16px] font-semibold tracking-tight">
              {isNew ? 'New catalog entry' : (draft.locales.en?.title || recordQ.data?.defaultTitle || `Entry #${entryId}`)}
            </div>
            {!isNew && (
              <div className="mt-0.5 font-mono text-[11px] text-[color:var(--bo-mute)]">
                {recordQ.data?.slug}
              </div>
            )}
          </div>
          {!isNew && (
            <div className="flex items-center gap-2">
              <div className="text-end">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={retrieveMut.isPending || imageMut.isPending || entryId == null}
                    onClick={() => retrieveMut.mutate()}
                    title="Fill the EMPTY specs + description/SEO (all languages) from AI."
                    className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {retrieveMut.isPending ? 'Retrieving…' : 'Retrieve metadata (AI)'}
                  </button>
                  <button
                    type="button"
                    disabled={imageMut.isPending || retrieveMut.isPending || entryId == null}
                    onClick={() => imageMut.mutate()}
                    title="Generate a studio hero image with AI. Takes 1-2 minutes; replaces the current hero."
                    className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {imageMut.isPending ? 'Generating…' : 'Generate image (AI)'}
                  </button>
                </div>
                {retrieveError != null && (
                  <div className="mt-1 text-[11px] text-[#9f1239]">
                    {retrieveError === 'ai_not_configured'
                      ? 'AI is not configured yet.'
                      : retrieveError === 'image_still_generating'
                        ? 'Still generating — the image will appear when ready. Reload in a minute.'
                        : 'Retrieve failed.'}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="text-[12px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
              >
                Cancel
              </button>
            </div>
          )}
          {isNew && (
            <div className="flex items-center gap-2">
              <div className="text-end">
                <button
                  type="button"
                  disabled={generateMut.isPending || !draft.modelId}
                  onClick={() => generateMut.mutate()}
                  title="Fill the EMPTY specs + description/SEO (all languages) from the chosen model, year and trim."
                  className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generateMut.isPending ? 'Generating…' : 'Generate with AI'}
                </button>
                {retrieveError != null && (
                  <div className="mt-1 text-[11px] text-[#9f1239]">
                    {retrieveError === 'ai_not_configured'
                      ? 'AI is not configured yet.'
                      : 'Generate failed.'}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="text-[12px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Core fields */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Year" hint="Leave blank if the entry has no specific model year.">
            <input
              type="number"
              value={draft.year ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, year: e.target.value === '' ? null : Number(e.target.value) })
              }
              min={1950}
              max={2100}
              className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </Field>
          <Field label="Trim" hint="Leave blank for base trim.">
            <input
              type="text"
              value={draft.trim}
              onChange={(e) => setDraft({ ...draft, trim: e.target.value })}
              placeholder="e.g. SE, XLE, Sport"
              className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </Field>
          <Field label="Status">
            <label className="flex h-9 cursor-pointer items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                className="h-4 w-4 accent-[color:var(--bo-accent)]"
              />
              Active (visible to vendors)
            </label>
          </Field>
        </div>

        {/* Specs */}
        <div className="mt-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
            Specs
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Field label="Passengers">
              <input
                type="number"
                value={draft.specs.passenger ?? ''}
                onChange={(e) =>
                  setSpec('passenger', e.target.value === '' ? null : Number(e.target.value))
                }
                min={1}
                max={99}
                placeholder="—"
                className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </Field>
            <Field label="Doors">
              <input
                type="number"
                value={draft.specs.doors ?? ''}
                onChange={(e) =>
                  setSpec('doors', e.target.value === '' ? null : Number(e.target.value))
                }
                min={1}
                max={99}
                placeholder="—"
                className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </Field>
            <Field label="Baggage (items)">
              <input
                type="number"
                value={draft.specs.baggage ?? ''}
                onChange={(e) =>
                  setSpec('baggage', e.target.value === '' ? null : Number(e.target.value))
                }
                min={0}
                max={99}
                placeholder="—"
                className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </Field>
            <Field label="Gear shift">
              <SpecSelect
                value={draft.specs.gearShift ?? null}
                options={specOptions.gearShift}
                onChange={(v) => setSpec('gearShift', v)}
              />
            </Field>
            <Field label="Fuel type">
              <SpecSelect
                value={draft.specs.fuelType ?? null}
                options={specOptions.fuelType}
                onChange={(v) => setSpec('fuelType', v)}
              />
            </Field>
            <Field label="Body type">
              <SpecSelect
                value={draft.specs.bodyType ?? null}
                options={specOptions.bodyType}
                onChange={(v) => setSpec('bodyType', v)}
              />
            </Field>
          </div>
        </div>

        {/* Locale tabs */}
        <div className="mt-4">
          <div className="mb-2 flex flex-wrap items-center gap-1">
            {LOCALES.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLocale(l)}
                className={`rounded-md border px-2 py-1 text-[12px] font-medium ${
                  locale === l
                    ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
                    : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
                }`}
              >
                {LOCALE_LABEL[l]}
              </button>
            ))}
          </div>
          <div className="space-y-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <Field label={`Title (${LOCALE_LABEL[locale]})`}>
              <input
                type="text"
                value={draft.locales[locale]?.title ?? ''}
                onChange={(e) => setLocaleCopy(locale, 'title', e.target.value || undefined)}
                placeholder={locale === 'en' ? 'e.g. Toyota Camry 2024' : ''}
                className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </Field>
            <Field label={`Description (${LOCALE_LABEL[locale]})`}>
              <textarea
                value={draft.locales[locale]?.description ?? ''}
                onChange={(e) =>
                  setLocaleCopy(locale, 'description', e.target.value || null)
                }
                rows={3}
                className="w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label={`SEO title (${LOCALE_LABEL[locale]})`}>
                <input
                  type="text"
                  value={draft.locales[locale]?.seoMetaTitle ?? ''}
                  onChange={(e) =>
                    setLocaleCopy(locale, 'seoMetaTitle', e.target.value || null)
                  }
                  className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                />
              </Field>
              <Field label={`SEO description (${LOCALE_LABEL[locale]})`}>
                <input
                  type="text"
                  value={draft.locales[locale]?.seoMetaDescription ?? ''}
                  onChange={(e) =>
                    setLocaleCopy(locale, 'seoMetaDescription', e.target.value || null)
                  }
                  className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                />
              </Field>
            </div>
          </div>
        </div>
      </div>

      {/* Feature multi-select */}
      <FeaturePanel
        selectedIds={draft.featureIds}
        onChange={(ids) => setDraft((d) => ({ ...d, featureIds: ids }))}
      />

      {/* Image manager — only shown on saved entries */}
      {!isNew && entryId != null && (
        <ImageManager
          entryId={entryId}
          images={recordQ.data?.images ?? []}
          onRefresh={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['admin', 'vehicle-catalog', 'entry', entryId],
            });
          }}
        />
      )}

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => {
          if (isNew) {
            setDraft({
              modelId: defaultModelId,
              year: new Date().getFullYear(),
              trim: '',
              isActive: true,
              specs: emptySpecs(),
              featureIds: [],
              locales: emptyLocales(),
            });
          } else {
            if (recordQ.data) setDraft(recordToDraft(recordQ.data));
          }
        }}
        onSave={() => saveMut.mutate()}
      />
    </>
  );
}

// ─── Feature panel ────────────────────────────────────────────────────

function FeaturePanel({
  selectedIds,
  onChange,
}: {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const [q, setQ] = useState('');
  const attribValQ = useQuery({
    queryKey: ['admin', 'attribute-values', 'lookup', q],
    queryFn: ({ signal }) => fetchAdminAttributeValues({ q: q || undefined, limit: 80 }, signal),
    staleTime: 60_000,
  });
  const rows = attribValQ.data?.rows ?? [];

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold tracking-tight">
          Features{' '}
          <span className="ms-1 font-normal text-[color:var(--bo-mute)]">
            ({selectedIds.length} selected)
          </span>
        </h3>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter attribute values…"
          className="h-8 w-[220px] rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
      </div>
      {attribValQ.isLoading ? (
        <div className="text-[12px] text-[color:var(--bo-mute)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12px] text-[color:var(--bo-mute)]">No attribute values found.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {rows.map((row) => {
            const isOn = selectedIds.includes(row.id);
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => toggle(row.id)}
                className={`rounded-full border px-2.5 py-1 text-[12px] font-medium transition ${
                  isOn
                    ? 'border-[color:var(--bo-accent)] bg-[color:var(--bo-accent)] text-white'
                    : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
                }`}
              >
                {row.name ?? row.slug}
                {row.attributeName ? (
                  <span className="ms-1 opacity-60">({row.attributeSlug})</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Image manager ────────────────────────────────────────────────────

function ImageManager({
  entryId,
  images,
  onRefresh,
}: {
  entryId: number;
  images: CatalogEntryImage[];
  onRefresh: () => void | Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const setHeroMut = useMutation({
    mutationFn: (imageId: number) => setCatalogImageHero(entryId, imageId),
    onSuccess: onRefresh,
  });

  const deleteImageMut = useMutation({
    mutationFn: (imageId: number) => deleteCatalogImage(entryId, imageId),
    onSuccess: onRefresh,
  });

  const reorderMut = useMutation({
    mutationFn: (order: number[]) => reorderCatalogImages(entryId, order),
    onSuccess: onRefresh,
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const presign = await presignCatalogImage(entryId, {
        originalFilename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': presign.contentType },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);
      await finalizeCatalogImage(entryId, {
        key: presign.key,
        originalFilename: file.name,
        contentType: presign.contentType,
        sizeBytes: file.size,
        hero: images.length === 0, // first image becomes hero
      });
      await onRefresh();
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold tracking-tight">Images</h3>
        <label className="inline-flex cursor-pointer items-center rounded-lg border border-[color:var(--bo-line)] px-3 py-1.5 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)]">
          {uploading ? 'Uploading…' : 'Upload image'}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={handleFileChange}
            className="sr-only"
          />
        </label>
      </div>
      {uploadError ? <ErrorPanel message={uploadError} /> : null}
      {images.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[color:var(--bo-line)] p-4 text-[12px] text-[color:var(--bo-mute)]">
          No images yet. Upload at least one.
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {images.map((img, idx) => (
            <div key={img.id} className="relative w-[120px]">
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]">
                <img
                  src={img.url}
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    // Silently hide broken images — source key may not be
                    // accessible from the backoffice origin.
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                {img.isHero && (
                  <span className="absolute start-1 top-1 rounded-full bg-[color:var(--bo-ink)] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    Hero
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-center justify-between gap-1">
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    title="Move left"
                    onClick={() => {
                      const order = images.map((i) => i.id);
                      if (idx === 0) return;
                      const next = [...order];
                      const a = next[idx - 1]!;
                      const b = next[idx]!;
                      next[idx - 1] = b;
                      next[idx] = a;
                      reorderMut.mutate(next);
                    }}
                    disabled={idx === 0}
                    className="inline-flex h-6 w-6 items-center justify-center rounded border border-[color:var(--bo-line)] text-[12px] disabled:opacity-40"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    title="Move right"
                    onClick={() => {
                      const order = images.map((i) => i.id);
                      if (idx === images.length - 1) return;
                      const next = [...order];
                      const a = next[idx]!;
                      const b = next[idx + 1]!;
                      next[idx] = b;
                      next[idx + 1] = a;
                      reorderMut.mutate(next);
                    }}
                    disabled={idx === images.length - 1}
                    className="inline-flex h-6 w-6 items-center justify-center rounded border border-[color:var(--bo-line)] text-[12px] disabled:opacity-40"
                  >
                    →
                  </button>
                </div>
                <div className="flex items-center gap-0.5">
                  {!img.isHero && (
                    <button
                      type="button"
                      title="Set as hero"
                      onClick={() => setHeroMut.mutate(img.id)}
                      className="inline-flex h-6 items-center rounded border border-[color:var(--bo-line)] px-1.5 text-[10px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)]"
                    >
                      Hero
                    </button>
                  )}
                  <button
                    type="button"
                    title="Delete image"
                    onClick={() => {
                      const ok = window.confirm('Delete this image?');
                      if (ok) deleteImageMut.mutate(img.id);
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded border border-[#fecaca] text-[12px] text-[#9f1239] hover:bg-[#fff1f2]"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────

// Select constrained to the canonical attribute-value names (transmission /
// fuel-type / vehicle-type). A stored value the taxonomy doesn't know (legacy
// free text or AI output from before normalization) stays selectable, marked
// "(legacy)", so opening an old entry never silently drops data. Falls back
// to free text while the options list hasn't loaded.
function SpecSelect({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: string[];
  onChange: (value: string | null) => void;
}) {
  const inputClass =
    'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]';
  if (options.length === 0) {
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder="—"
        className={inputClass}
      />
    );
  }
  const current = value ?? '';
  const legacy = current !== '' && !options.includes(current);
  return (
    <select value={current} onChange={(e) => onChange(e.target.value || null)} className={inputClass}>
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
      {legacy && <option value={current}>{current} (legacy)</option>}
    </select>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-[color:var(--bo-mute)]">{hint}</p> : null}
    </div>
  );
}

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky bottom-2 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
    >
      {message}
    </div>
  );
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}
